import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { DialogClose, DialogTitle } from '@radix-ui/react-dialog';

import { CalendarIcon, ChatIcon } from '@/assets/icons';
import { Button } from '@/components/atoms';
import { DialogContent } from '@/components/atoms/Dialog/Dialog';
import { formatRowDate } from '@/utils/formatRowDate';

import { FilterDropdown, FilterDropdownOption } from '../Dropdowns/FilterDropdown/FilterDropdown';

type ChangeQuestionPopupProps = {
  question: FilterDropdownOption;
  scheduleDate: string;
  lastAsked?: string | null;
  categories: FilterDropdownOption[];
  questions: FilterDropdownOption[];
  isQuestionsLoading?: boolean;
  selectedCategory: FilterDropdownOption | null;
  onCategoryChange: (value: FilterDropdownOption | null) => void;
  selectedQuestion: FilterDropdownOption | null;
  onQuestionChange: (value: FilterDropdownOption | null) => void;
  onChangeQuestion: () => void;
  isSubmitDisabled?: boolean;
  isSubmitLoading?: boolean;
  errorText?: string | null;
};

export const ChangeQuestionPopup: FC<ChangeQuestionPopupProps> = ({
  lastAsked,
  scheduleDate,
  question,
  categories,
  questions,
  isQuestionsLoading,
  selectedCategory,
  selectedQuestion,
  onCategoryChange,
  onQuestionChange,
  onChangeQuestion,
  isSubmitDisabled,
  isSubmitLoading,
  errorText,
}) => {
  const { full: scheduleDateFormatted } = formatRowDate(scheduleDate);
  const { full: fullDateLastAsked } = formatRowDate(lastAsked);
  const { t } = useTranslation('components');

  const lastAskedDisplay = fullDateLastAsked ?? t('changeQuestionPopup.questionHistory.empty');

  const hintText = t('changeQuestionPopup.questionHistory.lastAskedHint', {
    date: lastAskedDisplay,
  });

  const questionDropdownDisabled = !selectedCategory || Boolean(isQuestionsLoading);
  const submitDisabled =
    Boolean(isSubmitDisabled) || !selectedCategory || !selectedQuestion || Boolean(isSubmitLoading);

  return (
    <DialogContent showCloseButton className="w-[460px]">
      <DialogTitle className="mb-3 text-body-20-semibold text-custom-warm-grey-800">
        {t('changeQuestionPopup.title')}
      </DialogTitle>
      <div className="mb-5">
        <div className="mb-2 flex items-center gap-2">
          <ChatIcon width={20} height={20} className="text-custom-warm-grey-400" />
          <p className="text-body-14-regular text-custom-warm-grey-700">{question.label}</p>
        </div>
        <div className="flex items-center gap-2">
          <CalendarIcon width={20} height={20} className="text-custom-warm-grey-400" />
          <p className="text-body-14-regular text-custom-warm-grey-700">
            {scheduleDateFormatted ?? t('changeQuestionPopup.questionHistory.empty')}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-5">
        <FilterDropdown
          label={t('changeQuestionPopup.inputs.labelCategory')}
          options={categories}
          value={selectedCategory}
          onChange={onCategoryChange}
          triggerClassName="w-full"
          className="w-full"
          placeholder={t('changeQuestionPopup.inputs.placeholderCategory')}
        />
        <FilterDropdown
          label={t('changeQuestionPopup.inputs.labelQuestion')}
          options={questions}
          value={selectedQuestion}
          onChange={onQuestionChange}
          disabled={questionDropdownDisabled}
          triggerClassName="w-full"
          className="w-full"
          placeholder={t('changeQuestionPopup.inputs.placeholderQuestion')}
          hintText={hintText}
        />
      </div>

      {errorText ? (
        <p className="mt-3 text-body-12-regular text-custom-red-500" role="alert">
          {t(errorText)}
        </p>
      ) : null}

      <div className="flex items-center gap-3 mt-5">
        <DialogClose asChild>
          <Button size="lg" variant="secondary" className="w-full">
            {t('changeQuestionPopup.buttons.cancelButton')}
          </Button>
        </DialogClose>
        <Button
          size="lg"
          variant="primary"
          className="w-full"
          onClick={onChangeQuestion}
          disabled={submitDisabled}
        >
          {t('changeQuestionPopup.buttons.changeButton')}
        </Button>
      </div>
    </DialogContent>
  );
};
